import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import ArrowDownwardRounded from "@mui/icons-material/ArrowDownwardRounded";
import ArrowUpwardRounded from "@mui/icons-material/ArrowUpwardRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import {
  PROMPT_GRAPH_NODE_TYPES,
  collectAllNodeIds,
} from "services/fde/tciGovernanceService";

const INPUT_SOURCES = ["transcript", "prior_output", "combined", "state"];
const OUTPUT_TARGETS = ["next_node", "state", "finalize"];
const COMPOSITION_MODES = ["substitutionary", "sequential"];
const RETRIEVAL_TYPES = ["embedded_documents", "clinical_references", "cmr", "other"];
const CONDITION_TYPES = ["skip_guard", "branch", "feature_flag"];
const MERGE_STRATEGIES = ["all_required", "first_complete", "best_effort"];
const MODALITIES = ["audio", "text", "multimodal"];

function blankInferenceNode(order) {
  return {
    nodeId: `node-${Date.now()}`,
    nodeType: "inference",
    label: "New node",
    description: "",
    order,
    promptId: "",
    inputSource: "transcript",
    outputTarget: "next_node",
    compositionMode: "substitutionary",
    ragEnabled: false,
  };
}

function primaryIdentifier(node) {
  if (!node) return "—";
  if (node.nodeType === "inference") return node.promptId || "—";
  if (node.nodeType === "script") return node.queryId || "Deterministic";
  if (node.nodeType === "stream") return node.transport || "—";
  if (node.nodeType === "retrieval") return node.retrievalType || "—";
  return node.nodeId;
}

function NodeFields({
  node,
  onChange,
  graph,
  pathPrefix = "",
}) {
  const allNodeIds = collectAllNodeIds(graph).filter((id) => id !== node.nodeId);

  const update = (patch) => onChange({ ...node, ...patch });

  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <TextField
        label="Label"
        value={node.label || ""}
        onChange={(e) => update({ label: e.target.value })}
        size="small"
      />
      <FormControl size="small">
        <InputLabel id={`${pathPrefix}-nodeType`}>Node type</InputLabel>
        <Select
          labelId={`${pathPrefix}-nodeType`}
          label="Node type"
          value={node.nodeType || "inference"}
          onChange={(e) => update({ nodeType: e.target.value })}
        >
          {PROMPT_GRAPH_NODE_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        label="Description"
        value={node.description || ""}
        onChange={(e) => update({ description: e.target.value })}
        size="small"
        multiline
        minRows={2}
      />

      {node.nodeType === "inference" && (
        <>
          <TextField
            label="promptId"
            value={node.promptId || ""}
            onChange={(e) => update({ promptId: e.target.value })}
            size="small"
          />
          <FormControl size="small">
            <InputLabel>inputSource</InputLabel>
            <Select
              label="inputSource"
              value={node.inputSource || "transcript"}
              onChange={(e) => update({ inputSource: e.target.value })}
            >
              {INPUT_SOURCES.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>outputTarget</InputLabel>
            <Select
              label="outputTarget"
              value={node.outputTarget || "next_node"}
              onChange={(e) => update({ outputTarget: e.target.value })}
            >
              {OUTPUT_TARGETS.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>compositionMode</InputLabel>
            <Select
              label="compositionMode"
              value={node.compositionMode || "substitutionary"}
              onChange={(e) => update({ compositionMode: e.target.value })}
            >
              {COMPOSITION_MODES.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(node.ragEnabled)}
                onChange={(e) => update({ ragEnabled: e.target.checked })}
              />
            }
            label="ragEnabled"
          />
        </>
      )}

      {node.nodeType === "script" && (
        <>
          <TextField
            label="queryId"
            value={node.queryId || ""}
            onChange={(e) => update({ queryId: e.target.value })}
            size="small"
          />
          <TextField
            label="scriptKey"
            value={node.scriptKey || ""}
            onChange={(e) => update({ scriptKey: e.target.value })}
            size="small"
          />
        </>
      )}

      {node.nodeType === "stream" && (
        <>
          <TextField
            label="transport"
            value={node.transport || ""}
            onChange={(e) => update({ transport: e.target.value })}
            size="small"
          />
          <FormControl size="small">
            <InputLabel>inputModality</InputLabel>
            <Select
              label="inputModality"
              value={node.inputModality || "audio"}
              onChange={(e) => update({ inputModality: e.target.value })}
            >
              {MODALITIES.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>outputModality</InputLabel>
            <Select
              label="outputModality"
              value={node.outputModality || "audio"}
              onChange={(e) => update({ outputModality: e.target.value })}
            >
              {MODALITIES.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="sessionInitCallable"
            value={node.sessionInitCallable || ""}
            onChange={(e) => update({ sessionInitCallable: e.target.value })}
            size="small"
          />
          <FormControl size="small">
            <InputLabel>companionNodeIds</InputLabel>
            <Select
              multiple
              label="companionNodeIds"
              value={node.companionNodeIds || []}
              onChange={(e) =>
                update({ companionNodeIds: typeof e.target.value === "string" ? [] : e.target.value })
              }
              renderValue={(selected) => selected.join(", ")}
            >
              {allNodeIds.map((id) => {
                const match = graph.find((row) => row.nodeId === id);
                return (
                  <MenuItem key={id} value={id}>
                    {match?.label ? `${match.label} (${id})` : id}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={Boolean(node.persistent)}
                onChange={(e) => update({ persistent: e.target.checked })}
              />
            }
            label="persistent"
          />
        </>
      )}

      {node.nodeType === "retrieval" && (
        <>
          <FormControl size="small">
            <InputLabel>retrievalType</InputLabel>
            <Select
              label="retrievalType"
              value={node.retrievalType || "embedded_documents"}
              onChange={(e) => update({ retrievalType: e.target.value })}
            >
              {RETRIEVAL_TYPES.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>outputTarget</InputLabel>
            <Select
              label="outputTarget"
              value={node.outputTarget || "next_node"}
              onChange={(e) => update({ outputTarget: e.target.value })}
            >
              {OUTPUT_TARGETS.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="callableOrService"
            value={node.callableOrService || ""}
            onChange={(e) => update({ callableOrService: e.target.value })}
            size="small"
          />
        </>
      )}

      {node.nodeType === "conditional" && (
        <>
          <FormControl size="small">
            <InputLabel>conditionType</InputLabel>
            <Select
              label="conditionType"
              value={node.conditionType || "skip_guard"}
              onChange={(e) => update({ conditionType: e.target.value })}
            >
              {CONDITION_TYPES.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="conditionDescription"
            value={node.conditionDescription || ""}
            onChange={(e) => update({ conditionDescription: e.target.value })}
            size="small"
            multiline
            minRows={2}
          />
          {(node.branches || []).map((branch, index) => (
            <Stack key={index} direction="row" spacing={1} alignItems="center">
              <TextField
                label="Branch label"
                value={branch.label || ""}
                size="small"
                onChange={(e) => {
                  const branches = [...(node.branches || [])];
                  branches[index] = { ...branches[index], label: e.target.value };
                  update({ branches });
                }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={branch.child == null}
                    onChange={(e) => {
                      const branches = [...(node.branches || [])];
                      branches[index] = {
                        ...branches[index],
                        child: e.target.checked ? null : blankInferenceNode(0),
                      };
                      update({ branches });
                    }}
                  />
                }
                label="Skip (no child)"
              />
            </Stack>
          ))}
          <Button
            size="small"
            onClick={() =>
              update({
                branches: [...(node.branches || []), { label: "Branch", child: null }],
              })
            }
          >
            Add branch
          </Button>
        </>
      )}

      {(node.nodeType === "sequence" || node.nodeType === "parallel") && (
        <Stack spacing={1}>
          {node.nodeType === "parallel" && (
            <FormControl size="small">
              <InputLabel>mergeStrategy</InputLabel>
              <Select
                label="mergeStrategy"
                value={node.mergeStrategy || "all_required"}
                onChange={(e) => update({ mergeStrategy: e.target.value })}
              >
                {MERGE_STRATEGIES.map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {(node.children || []).map((child, index) => (
            <Paper key={child.nodeId || index} variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Child {index + 1}
              </Typography>
              <NodeFields
                node={child}
                graph={graph}
                pathPrefix={`${pathPrefix}-child-${index}`}
                onChange={(nextChild) => {
                  const children = [...(node.children || [])];
                  children[index] = nextChild;
                  update({ children });
                }}
              />
            </Paper>
          ))}
          <Button
            size="small"
            onClick={() =>
              update({
                children: [
                  ...(node.children || []),
                  { ...blankInferenceNode((node.children || []).length), nodeId: `child-${Date.now()}` },
                ],
              })
            }
          >
            Add child
          </Button>
        </Stack>
      )}

      {node.nodeType === "loop" && (
        <>
          <TextField
            label="exitCondition"
            value={node.exitCondition || ""}
            onChange={(e) => update({ exitCondition: e.target.value })}
            size="small"
          />
          <TextField
            label="maxIterations"
            type="number"
            inputProps={{ min: 1, max: 100 }}
            value={node.maxIterations ?? 20}
            onChange={(e) => update({ maxIterations: Number(e.target.value) })}
            size="small"
          />
          {node.child && (
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Loop child
              </Typography>
              <NodeFields
                node={node.child}
                graph={graph}
                pathPrefix={`${pathPrefix}-loop-child`}
                onChange={(nextChild) => update({ child: nextChild })}
              />
            </Paper>
          )}
        </>
      )}
    </Stack>
  );
}

const PromptGraphEditor = ({
  graph = [],
  readOnly = false,
  onSave,
  saving = false,
  canEdit = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(graph);
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const sortedRead = useMemo(
    () => [...(graph || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [graph],
  );

  const staleCompanionRefs = useMemo(() => {
    const ids = new Set(collectAllNodeIds(graph));
    const stale = [];
    graph.forEach((node) => {
      if (node.nodeType !== "stream") return;
      (node.companionNodeIds || []).forEach((refId) => {
        if (!ids.has(refId)) stale.push({ nodeId: node.nodeId, refId });
      });
    });
    return stale;
  }, [graph]);

  const startEdit = () => {
    setDraft(JSON.parse(JSON.stringify(graph || [])));
    setEditing(true);
    setErrorMessage("");
    setConfirmRemoveId(null);
  };

  const cancelEdit = () => {
    setDraft(graph);
    setEditing(false);
    setConfirmRemoveId(null);
    setErrorMessage("");
  };

  const moveNode = (index, direction) => {
    const next = [...draft].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setDraft(next.map((node, order) => ({ ...node, order })));
  };

  const updateNode = (index, node) => {
    const next = [...draft];
    next[index] = node;
    setDraft(next);
  };

  const removeNode = (index) => {
    const next = draft.filter((_, i) => i !== index).map((node, order) => ({ ...node, order }));
    setDraft(next);
    setConfirmRemoveId(null);
  };

  const handleSave = async () => {
    setErrorMessage("");
    try {
      const normalized = [...draft]
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((node, order) => ({ ...node, order }));
      await onSave(normalized);
      setEditing(false);
    } catch (err) {
      setErrorMessage(err?.message || "Failed to save prompt graph.");
    }
  };

  if (!canEdit && !sortedRead.length) return null;

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Prompt Graph
      </Typography>

      {staleCompanionRefs.length > 0 && !editing && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Referenced node not found:{" "}
          {staleCompanionRefs.map((row) => `${row.refId} (on ${row.nodeId})`).join(", ")}
        </Alert>
      )}

      {!editing ? (
        <>
          {sortedRead.length ? (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Order</TableCell>
                  <TableCell>Label</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Primary ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRead.map((node) => (
                  <TableRow key={node.nodeId}>
                    <TableCell>{node.order}</TableCell>
                    <TableCell>{node.label}</TableCell>
                    <TableCell>
                      <Chip size="small" label={node.nodeType} />
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace" }}>{primaryIdentifier(node)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No prompt graph defined yet.
            </Typography>
          )}
          {canEdit && (
            <Button sx={{ mt: 2 }} variant="outlined" onClick={startEdit}>
              Edit prompt graph
            </Button>
          )}
        </>
      ) : (
        <Stack spacing={2}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          {[...draft]
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
            .map((node, index) => (
              <Paper key={node.nodeId} variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <IconButton size="small" onClick={() => moveNode(index, -1)} aria-label="Move up">
                    <ArrowUpwardRounded fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => moveNode(index, 1)} aria-label="Move down">
                    <ArrowDownwardRounded fontSize="small" />
                  </IconButton>
                  <Typography variant="subtitle2" sx={{ flex: 1 }}>
                    {node.label || node.nodeId}
                  </Typography>
                  {confirmRemoveId === node.nodeId ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" color="error">
                        Remove this node?
                      </Typography>
                      <Button size="small" color="error" onClick={() => removeNode(index)}>
                        Confirm
                      </Button>
                      <Button size="small" onClick={() => setConfirmRemoveId(null)}>
                        Cancel
                      </Button>
                    </Stack>
                  ) : (
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setConfirmRemoveId(node.nodeId)}
                      aria-label="Remove node"
                    >
                      <DeleteOutlineRounded fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <TextField
                  label="nodeId"
                  value={node.nodeId || ""}
                  size="small"
                  fullWidth
                  sx={{ mb: 1 }}
                  onChange={(e) => updateNode(index, { ...node, nodeId: e.target.value })}
                />
                <NodeFields
                  node={node}
                  graph={draft}
                  pathPrefix={`top-${index}`}
                  onChange={(next) => updateNode(index, next)}
                />
              </Paper>
            ))}
          <Button
            variant="outlined"
            onClick={() =>
              setDraft([
                ...draft,
                blankInferenceNode(draft.length),
              ])
            }
          >
            Add node
          </Button>
          <Stack direction="row" spacing={1}>
            <LoadingButton variant="contained" loading={saving} onClick={handleSave}>
              Save prompt graph
            </LoadingButton>
            <Button onClick={cancelEdit}>Cancel</Button>
          </Stack>
        </Stack>
      )}
    </Box>
  );
};

export default PromptGraphEditor;
